import { useEffect, useState } from "react";
import { useNavigate, Outlet, useMatch } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { QrReader } from "react-qr-reader";
import jsQR from "jsqr";
import Typography from "@mui/material/Typography";

import { useDigitalReceptionMetadata } from "../../lib/hooks/useQueryBoard";
import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import Curve from "../../components/Animations/PageTransitions/Curve";
import HidScannerInput from "../../components/HidScannerInput";

// import usePageTracking from "../../bit/tracking/usePageTracking";

export const ViewFinder = () => (
  <svg
    viewBox="0 0 100 100"
    style={{
      top: 0,
      left: 0,
      zIndex: 1,
      boxSizing: "border-box",
      position: "absolute",
      width: "100%",
      height: "100%",
    }}
  >
    <path
      fill="none"
      d="M13,0 L0,0 L0,13"
      stroke="rgba(255, 0, 0, 0.5)"
      strokeWidth="0.5rem"
    />
    <path
      fill="none"
      d="M0,87 L0,100 L13,100"
      stroke="rgba(255, 0, 0, 0.5)"
      strokeWidth="0.5rem"
    />
    <path
      fill="none"
      d="M87,100 L100,100 L100,87"
      stroke="rgba(255, 0, 0, 0.5)"
      strokeWidth="0.5rem"
    />
    <path
      fill="none"
      d="M100,13 L100,0 87,0"
      stroke="rgba(255, 0, 0, 0.5)"
      strokeWidth="0.5rem"
    />
  </svg>
);

const QRCodeScanner = () => {
  const { t } = useTranslation(["QRCodeScanner"]);

  const match = useMatch({ path: "/scanner/:action" });

  const action = match?.params?.action;

  const navigate = useNavigate();
  const { qrScannerRotation = 0 } = useDigitalReceptionMetadata();

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const scanQrCode = () => {
      try {
        setError(null);

        const video = document.getElementById(
          "qr-video",
        ) as HTMLVideoElement | null;

        if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx?.translate(canvas.width, 0);
        ctx?.scale(-1, 1);
        ctx?.drawImage(
          video as unknown as CanvasImageSource,
          0,
          0,
          canvas.width,
          canvas.height,
        );

        const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);

        if (!imageData) return;

        const code = jsQR(imageData.data, canvas.width, canvas.height);

        if (code) {
          const data = code.data.trim();
          const regex = /^\d+$/;

          if (regex.test(data) && data.length === 6) {
            navigate(`/access/${action}/${data}`);
          } else {
            setError(t("QRCodeScanner:error"));
          }
        }
      } catch (err) {
        setError(t("QRCodeScanner:unknownError"));
      }
    };

    const interval = setInterval(scanQrCode, 500);

    return () => clearInterval(interval);
  }, [navigate, action]);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to={`/access/${action}`} />
        <Title>
          <Typography variant="h1">
            <Trans i18nKey="QRCodeScanner:title" />
          </Typography>
          <Typography variant="h4" className="font-weight-regular mt-5">
            <Trans i18nKey="QRCodeScanner:subtitle" />
          </Typography>
        </Title>
        <div className="qr-scanner">
          <div
            style={{
              width: "100%",
              margin: "auto",
              transform: `rotate(${qrScannerRotation}deg)`,
            }}
          >
            <QrReader
              videoId="qr-video"
              constraints={{ facingMode: "user", aspectRatio: 1 }}
              ViewFinder={ViewFinder}
            />
          </div>
          {error && (
            <Typography
              variant="h3"
              className="position-absolute text-center text-error p-4"
            >
              {error}
            </Typography>
          )}
        </div>
      </div>
      <HidScannerInput
        logTag="qr-scanner-camera-fallback"
        validate={(code) => /^\d+$/.test(code) && code.length === 6}
        onScan={(code) => navigate(`/access/${action}/${code}`)}
      />
      <Outlet />
    </Curve>
  );
};

export default QRCodeScanner;
